import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { IconButton, Icons, InformationModal } from 'utoniq-ui'

import { GpsData, Maybe } from '@/clients/utoniq-core/schema'
import { GpsMap } from '@/components/organisms/functionals'
import { GpsClientInfo, GpsMapModal } from '@/components/organisms/gps'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { openSetting } from '@/core/native/app'
import { usePrivateAction } from '@/lib/privateAction'

type Props = {
  gpsClientInfo: GpsClientInfo
  gpsGachaData:
    | Maybe<
        Pick<
          GpsData,
          'radius' | 'approximateAddress' | 'latitude' | 'longitude'
        >
      >
    | undefined
  isInside: boolean
}

/**
 * @description
 * GPSガチャ用情報表示エリア
 */
export const GpsInformation: Cell<Props> = ({
  gpsClientInfo,
  gpsGachaData,
  isInside,
}) => {
  const { t } = useTranslation('gachaDetail')
  const { handleLoginModal } = usePrivateAction()
  const [openMap, setOpenMap] = useState(false)
  const [openRequestPermission, setOpenRequestPermission] = useState(false)

  if (!gpsGachaData) return null

  return (
    <Stack id="gps-info-cell" alignItems="center" sx={{ pt: 5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ pb: 2 }}>
        {t('gpsDescription')}
      </Typography>
      <Box sx={{ px: 5, pb: 5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            px: 3,
            py: '6px',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: 'grey.300',
            borderRadius: '2px',
          }}
        >
          {t('areaName', { areaName: gpsGachaData?.approximateAddress })}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {gpsClientInfo.permission ? (
          <>
            <IconButton.Zoom
              size="medium"
              onClick={handleLoginModal(() => {
                setOpenMap(true)
              })}
              bgcolor="grey.50"
              color="grey.600"
              sx={{
                zIndex: 2,
                position: 'absolute',
                right: 12,
                top: 12,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                zIndex: 2,
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <AreaStatusChip isInside={isInside} />
              <GpsMapModal
                open={openMap}
                onClose={() => setOpenMap(false)}
                targetData={gpsGachaData}
                currentData={gpsClientInfo.currentLocation}
                isInside={isInside}
              />
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                position: 'absolute',
                zIndex: 1,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.72)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                zIndex: 2,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <Stack
                flex={1}
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100%' }}
              >
                <Typography variant="h6" align="center" color="white">
                  {t('notGpsPermission')}
                </Typography>
                <Button
                  sx={{ my: 3 }}
                  startIcon={<Icons.Window fontSize="large" color="inherit" />}
                  onClick={handleLoginModal(() => {
                    openSetting({
                      webSetting: () => setOpenRequestPermission(true),
                    })
                  })}
                >
                  {t('toSetting')}
                </Button>
              </Stack>
            </Box>
          </>
        )}
        {gpsGachaData?.latitude && gpsGachaData?.longitude && (
          <GpsMap
            targetData={gpsGachaData}
            currentData={gpsClientInfo.currentLocation}
            mapContainerStyle={{
              height: 250,
              width: '100%',
            }}
          />
        )}
      </Box>

      <RequestPermissionModal
        open={openRequestPermission}
        onClose={() => setOpenRequestPermission(false)}
      />
    </Stack>
  )
}

const AreaStatusChip: Cell<{ isInside: boolean }> = ({ isInside }) => {
  const { t } = useTranslation('gachaDetail')

  return (
    <CellFrame>
      <Chip
        color={isInside ? 'primary' : 'error'}
        label={
          <Typography variant="subtitle2" color="white">
            {isInside ? t('areaExist') : t('areaNotExist')}
          </Typography>
        }
        sx={{ height: 24 }}
      />
    </CellFrame>
  )
}

const RequestPermissionModal: FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useTranslation('gachaDetail')

  return (
    <InformationModal
      open={open}
      onClose={onClose}
      title={
        <>
          <Icons.Spot sx={{ width: 60, height: 60 }} />
          <Typography variant="h5" sx={{ py: 3 }}>
            {t('permissionModalTitle')}
          </Typography>
        </>
      }
      content={t('permissionModalContent')}
      buttons={
        <>
          <Button
            sx={{ minWidth: 212 }}
            variant="text"
            color="primary"
            size="large"
            onClick={onClose}
          >
            {t('close')}
          </Button>
        </>
      }
    />
  )
}
